// ════════════════════════════════════════════════════════════════════════════
//  ManDana — drawing a manifestation
//  ---------------------------------------------------------------------------
//  THIS IS THE ONLY FILE IN THE APP THAT MAY BUILD A srcdoc FRAME, and
//  tests/manifest-frame.test.js enforces that by scanning the source. It is a
//  file of its own rather than a branch inside md-embed.jsx for a reason worth
//  keeping: md-embed's video player legitimately sets allow-same-origin — that
//  frame is same-origin with youtube-nocookie.com rather than with us, and will
//  not load without it — so a rule saying "the file that builds srcdoc frames
//  may never say allow-same-origin" could not be stated at all if the two lived
//  together. Separating them lets the gate be a sentence instead of a judgement.
//
//  ── THE FRAME IS DISPOSABLE; THE BRIDGE IS THE RECORD ──────────────────────
//  The document string is built ONCE per instance and held, never recomputed in
//  render. That is not a performance note. Assigning a different string to
//  srcdoc RELOADS the frame, so a document rebuilt on every render — a fresh
//  session id, a timestamp, anything — would restart the manifestation
//  underneath whoever was using it. It is the same failure the compose sheet had
//  when it re-seeded from a stale snapshot on remount (see mdRecordLatest), and
//  the same answer: decide once what this thing opens on, and keep it.
//
//  What makes that survivable is the rule manifestations are written to: keep
//  nothing durable in the DOM. Read your state when you load, write it when it
//  changes, and a reload is a redraw. Excalidraw helps here more than expected —
//  an embeddable that has entered the viewport once is never unmounted again,
//  only hidden and resized to 0×0 — but a board switch does remount everything,
//  and full screen deliberately swaps which copy is live.
//
//  ── THREE STATES, AND THE THIRD MUST NEVER HAPPEN ──────────────────────────
//  Looking / not on this device / running. The one that is forbidden is a blank
//  rectangle with no explanation, which is what a frame whose bytes are missing
//  would otherwise be. Same posture, and nearly the same words, as FileFrame:
//  the bytes are device-local, so a manifestation built on a laptop is genuinely
//  absent on a phone, and saying so is the honest interface.
// ════════════════════════════════════════════════════════════════════════════

const { useState: useStateM, useEffect: useEffectM, useRef: useRefM } = React;
const { ink: inkM } = window;

/* What each loaded manifestation's bytes hash to, so the board's menu can grant
   against the code that is actually running rather than against a name. Module
   scope rather than a React context: it is a fact about bytes, one entry per
   instance, and it must survive a card being drawn and redrawn. */
const MD_MANIFEST_DIGESTS = new Map();
function mdManifestDigestOf(instance) { return MD_MANIFEST_DIGESTS.get(instance) || ''; }

/* And what each loaded manifestation's bytes DECLARED — the same set the bridge
   enforces against, published so the permission menu can name the libraries and
   fields somebody is about to hand over.

   It exists because the alternative was to read entry.declarations, which is a
   stored claim: it survives a rollback of these bytes and it is the entry
   writer's word rather than the file's. A permission prompt that describes the
   file using anything other than the file is a prompt that can be made to lie.

   Cleared alongside declRef whenever the bytes are reloaded, so a swap that
   fails leaves the menu with nothing to say instead of with the last file's
   answer. An absent entry is not an error — it means "not loaded yet", and the
   board already has that state from the digest. */
const MD_MANIFEST_DECLS = new Map();
function mdManifestDeclsOf(instance) { return MD_MANIFEST_DECLS.get(instance) || null; }

function CraftFrame({ entry, light, title, onRead, onAsk, surface = 'board', scopeRef = null, stopped = false }) {
  // undefined = still looking · null = the bytes are not on this device
  const [doc, setDoc] = useStateM(undefined);
  const [dropped, setDropped] = useStateM([]);

  /* One session id for the life of this frame. Held in a ref rather than state
     because reading it must never be what causes a render, and minted lazily so
     a frame that never loads never asks the platform for randomness. */
  const sidRef = useRefM('');
  if (!sidRef.current) sidRef.current = window.mdCraftId ? window.mdCraftId() : '';

  const instance = entry && entry.instance;
  const file = entry && entry.file;

  useEffectM(() => {
    let alive = true;
    /* Cleared FIRST, before any early return can skip it. A declaration set is
       only ever true of one particular set of bytes, so it must not outlive
       them for even one message — a file swap that fails to load has to leave
       the bridge with nothing to enforce rather than with the previous file's
       permissions. */
    declRef.current = null;
    if (instance) MD_MANIFEST_DECLS.delete(instance);
    if (!instance || !file || !window.mdFileGet) { setDoc(null); return undefined; }
    setDoc(undefined);
    window.mdFileGet(file).then((blob) => {
      if (!alive) return;
      if (!blob) { setDoc(null); return; }
      return blob.arrayBuffer().then((buf) => {
        if (!alive) return null;
        const bytes = new Uint8Array(buf);
        /* The digest is of the BYTES AS STORED, taken before anything is parsed
           out of them — so it names the document somebody was shown and agreed
           to, not our reading of it. */
        return window.mdGrantDigest(bytes).then((d) => {
          if (!alive) return;
          MD_MANIFEST_DIGESTS.set(instance, d);
          digestRef.current = d;
          const raw = new TextDecoder().decode(bytes);
          const source = window.mdCraftSource ? window.mdCraftSource(raw) : null;
          if (!source) { setDoc(null); return; }
          /* Set from the SAME parse as the digest above, so the thing the person
             consented to and the thing the bridge enforces are one document. The
             stored entry's copy is not consulted: it outlives a rollback of these
             bytes, and an entry that disagrees with its file would decide in
             favour of whichever was written last. */
          declRef.current = source.declarations || null;
          if (declRef.current) MD_MANIFEST_DECLS.set(instance, declRef.current);
          setDropped(source.dropped || []);
          setDoc(window.mdCraftDocument(source, sidRef.current, light, surface));
        });
      });
    }).catch(() => { if (alive) setDoc(null); });
    return () => { alive = false; };
    /* Keyed on WHAT IT IS, not on the entry object: md-app hands a fresh object
       down on every render, and depending on it would rebuild the document —
       which is to say reload the frame — several times a second.

       `light` is in here and it is the one dependency that DOES reload the frame,
       because the theme is baked into the document the host writes. That is the
       right trade: switching theme is a rare, deliberate act, and the alternative
       is a manifestation that stays in the wrong palette until it is reopened.
       It is only survivable because of the rule this file is built on — keep
       nothing durable in the DOM, so a reload is a redraw. */
  }, [instance, file, light, surface]);

  /* ── the host end of the bridge ───────────────────────────────────────────
     THE FRAME IS AUTHENTICATED BY ITS WINDOW, NOT BY ITS ORIGIN. There is no
     origin to check: a sandboxed frame with no allow-same-origin reports
     `event.origin` as the literal string "null", which any page anywhere can
     also present. `event.source === iframe.contentWindow` is the only thing that
     actually identifies the sender, and it is why this listener lives in the
     component that owns the frame rather than somewhere central.

     Every rule it applies lives in md-bridge.ts. This is wiring. */
  const frameRef = useRefM(null);
  const digestRef = useRefM('');
  const declRef = useRefM(null);
  const limitRef = useRefM(null);
  if (!limitRef.current) limitRef.current = window.mdBridgeLimiter();

  useEffectM(() => {
    if (!doc || !window.mdBridgeParse) return undefined;
    const onMessage = (e) => {
      const frame = frameRef.current;
      if (!frame || e.source !== frame.contentWindow) return;

      const req = window.mdBridgeParse(e.data, sidRef.current);
      if (!req || req.drop) return;                    // not placeable: say nothing

      const reply = (env) => {
        /* '*' is forced: an opaque origin cannot be named as a target. The
           session id inside the envelope is what makes the far end
           identifiable, so a frame that navigated away gets nothing it can use. */
        if (frameRef.current && frameRef.current.contentWindow) {
          frameRef.current.contentWindow.postMessage(env, '*');
        }
      };

      if (req.error) { reply(window.mdBridgeErr(sidRef.current, req.id, req.error)); return; }
      if (!limitRef.current(Date.now())) {
        reply(window.mdBridgeErr(sidRef.current, req.id, 'rate_limited'));
        return;
      }

      /* Read FRESH, every message. Holding the grant in state or a closure is
         how "revoked" comes to mean "revoked after a reload", and the whole
         value of a revoke is that it is immediate. */
      const grant = window.mdGrantRead(entry.instance);
      if (window.mdCraftCapabilityAllows &&
          !window.mdCraftCapabilityAllows(entry, req.op)) {
        reply(window.mdBridgeErr(sidRef.current, req.id, 'denied'));
        return;
      }
      if (!window.mdGrantAllows(grant, req.op, digestRef.current)) {
        reply(window.mdBridgeErr(sidRef.current, req.id, 'denied'));
        /* Say what it wanted, out here, once it has actually asked. A
           manifestation that never reaches for anything never interrupts. */
        if (onAsk) onAsk(entry, window.MD_BRIDGE_OPS[req.op]);
        return;
      }

      /* A manifestation's OWN store is answered here rather than by the app.
         Nothing outside this frame can see those rows, so there is nobody to ask
         and nothing for md-app to arbitrate — and keeping it local means the
         app's read server stays a list of things about the JOURNAL. */
      const serve = () => {
        if (req.op === 'app.context') return {
          surface,
          scopeRef,
          theme: light ? 'light' : 'dark',
          permissions: grant.level,
          capabilities: Array.isArray(entry.capabilities) ? entry.capabilities.slice() : null,
        };
        if (req.op === 'source.read') return window.mdCraftSourceRows(declRef.current, req.arg && req.arg.name);
        if (req.op === 'action.invoke') return window.mdCraftActionInvoke(declRef.current, req.arg && req.arg.name, req.arg && req.arg.values);
        if (req.op === 'data.get') return window.mdMStoreGet(entry.instance);
        if (req.op === 'data.put') {
          return window.mdMStorePut(entry.instance, req.arg.value).then((r) => {
            if (!r.ok) throw r.error;
            return { ok: true };
          });
        }
        return onRead(req.op, req.arg, { instance: entry.instance,
          name: entry.name, digest: digestRef.current, surface });
      };

      /* Promise.resolve().then(serve) rather than calling serve() directly, so a
         SYNCHRONOUS throw from the app's read server lands in the same rejection
         path as an asynchronous one. Two error paths for one idea is how one of
         them ends up untested. */
      Promise.resolve().then(serve).then(
        (result) => reply(window.mdBridgeOk(sidRef.current, req.id, result)),
        /* The server throws a CODE, never a sentence — md-bridge.ts owns the
           words, so an unknown throw becomes `internal` rather than putting a
           stack trace across the bridge. */
        (code) => reply(window.mdBridgeErr(sidRef.current, req.id, code)));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [doc, entry, onRead, onAsk, light, surface, scopeRef]);

  /* Host events are invalidations, not data payloads. The manifestation asks
     through the ordinary bridge when it wants fresh records, so permissions
     and field-level output remain enforced in one place. */
  useEffectM(() => {
    const forward = (name) => (event) => {
      const detail = event && event.detail;
      if (detail && detail.instance && detail.instance !== instance) return;
      const frame = frameRef.current;
      if (frame && frame.contentWindow) frame.contentWindow.postMessage({
        md: 'manifest', v: 1, sid: sidRef.current, event: name,
        detail: detail || {},
      }, '*');
    };
    const permissions = forward('permissions.changed');
    const records = forward('records.changed');
    window.addEventListener('md:craft-permissions', permissions);
    window.addEventListener('md:records-changed', records);
    return () => {
      window.removeEventListener('md:craft-permissions', permissions);
      window.removeEventListener('md:records-changed', records);
    };
  }, [instance, doc]);

  const box = {
    width: '100%', height: '100%', boxSizing: 'border-box', display: 'block',
    borderRadius: 20, overflow: 'hidden',
    border: `0.75px solid ${inkM(light ? 0.1 : 0.16)}`,
    background: light ? '#ffffff' : '#1a1c24',
  };

  if (doc && !stopped) {
    return (
      <div style={{ ...box, position: 'relative' }}>
        <iframe
          ref={frameRef}
          srcDoc={doc}
          title={title || (entry && entry.name) || 'Craft'}
          sandbox={window.MD_CRAFT_FRAME.sandbox}
          referrerPolicy={window.MD_CRAFT_FRAME.referrerPolicy}
          allow={window.MD_CRAFT_FRAME.allow}
          style={{ width: '100%', height: '100%', display: 'block', border: 'none',
            background: 'transparent' }} />
        {/* What the document asked for and cannot have. Quiet, in the corner, and
            only when there is something to say — a manifestation that reaches for
            a CDN chart library will otherwise just look broken, and "broken" is
            the one thing an interface must never leave somebody to guess at. */}
        {dropped.length > 0 && (
          <span style={{ position: 'absolute', left: 10, bottom: 10, pointerEvents: 'none',
            padding: '3px 8px', borderRadius: 8, fontFamily: 'var(--md-body)',
            fontSize: 11, fontWeight: 600, color: inkM(0.45),
            background: light ? 'rgba(255,255,255,0.86)' : 'rgba(26,28,36,0.86)',
            border: `0.75px solid ${inkM(light ? 0.1 : 0.16)}` }}>
            {dropped.length === 1 ? '1 external file blocked' : `${dropped.length} external files blocked`}
          </span>
        )}
      </div>);
  }

  return (
    <div style={{ ...box, background: 'transparent',
      border: `1px dashed ${inkM(light ? 0.14 : 0.2)}`,
      display: 'grid', placeItems: 'center', padding: 16, gap: 6,
      textAlign: 'center', fontFamily: 'var(--md-body)' }}>
      <span style={{ color: inkM(0.7), fontSize: 13.5, fontWeight: 600 }}>
        {(entry && entry.name) || 'Craft'}
      </span>
      <span style={{ color: inkM(0.32), fontSize: 12, fontWeight: 550, lineHeight: 1.5 }}>
        {stopped ? 'Stopped. Restart it from the card menu.'
          : doc === undefined ? 'Opening…' : 'This Craft is on the device it was added to'}
      </span>
    </div>);
}

window.CraftFrame = CraftFrame;
window.mdCraftDigestOf = mdManifestDigestOf;
window.mdCraftDeclsOf = mdManifestDeclsOf;
